/**
 * Browser-side TikTok Pixel helper.
 *
 * Mirrors how the Meta Pixel is fired (window.fbq), but for TikTok's `ttq`.
 * The `ttq` object is created by the base pixel code in _app.tsx; this helper
 * only fires standard events on top of it.
 *
 * Safe to call anywhere: it no-ops on the server and when the pixel has not
 * loaded yet (e.g. ad-blockers, env without TIKTOK_PIXEL_ID), so it can never
 * break a page or a checkout flow.
 *
 * The optional `eventId` is passed as TikTok's `event_id`. Reuse the SAME id
 * already generated for the Meta event at each call site — this keeps the
 * browser event ready to deduplicate against the server-side Events API once
 * the access token is configured.
 *
 * TikTok standard event names differ from Meta, e.g.:
 *   Meta Lead     -> TikTok SubmitForm
 */

export interface TikTokContent {
  content_id: string;
  content_type?: string;
  content_name?: string;
  quantity?: number;
  price?: number;
}

export interface TikTokTrackProperties {
  contents?: TikTokContent[];
  content_type?: string;
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
  query?: string;
  [key: string]: unknown;
}

export function trackTikTok(
  eventName: string,
  properties: TikTokTrackProperties = {},
  eventId?: string,
): void {
  if (typeof window === 'undefined') return;

  const ttq = (window as any).ttq;
  if (!ttq || typeof ttq.track !== 'function') return;

  try {
    if (eventId) {
      ttq.track(eventName, properties, { event_id: eventId });
    } else {
      ttq.track(eventName, properties);
    }
  } catch {
    // never let analytics throw into the calling flow
  }
}
